import React, {
  useState, useCallback, useEffect, forwardRef,
} from 'react';
import useLocale from '@/hooks/useLocale';
import * as Styles from './SSHAccess.styles';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import useServerStatus from '@/hooks/useServerStatus';
import { billingAnalytics } from '@/analytics';
import {
  Button, CopySnippet, Modal, Link,
} from 'gatorcomponents';

import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

import ContentSection from '@/pages/Products/VpsOciDetailPage/commons/ContentSection';
import ChangePasswordModal from '@/pages/Products/VpsOciDetailPage/components/ChangePasswordModal';

const SSHAccess = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const vpsOci = useSelector(state => state.vpsOci);
  const { ssh } = (vpsOci && vpsOci.data) || {};

  const hasPassword = ssh && typeof ssh.hasPassword !== 'undefined' ? ssh.hasPassword : true;
  const [newInstallation, setNewInstallation] = useState(!hasPassword);
  const [openSshTerminalError, setOpenSshTerminalError] = useState(false);
  const serverInfo = useSelector(state => state.vpsOci?.data?.serverInfo);

  const terminalIsLoading = vpsOci?.openSshTerminal?.loading || false;
  const urlTerminal = vpsOci?.openSshTerminal?.url || null;

  const { hostingAndServers: hostingAndServersAnalytics } = billingAnalytics;

  const { id: hostingId } = useParams();

  const {
    serverStatus,
  } = useServerStatus(serverInfo, hostingId);
  const isVpsOciOnline = serverStatus === 'online';

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(!hasPassword);
  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: localeVpsOci } = productDetailsLocale;

  const handleChangePassword = useCallback(() => {
    setShowChangePasswordModal(true);
  }, []);

  const handleOpenTerminal = useCallback(() => {
    dispatch(vpsOciActions.openSshTerminal.request(hostingId));

    const productName = vpsOci?.data?.productName;
    hostingAndServersAnalytics.hostingSshOpenTerminalClick(productName);
  }, [hostingId, dispatch, hostingAndServersAnalytics, vpsOci]);

  useEffect(() => {
    if (urlTerminal === 'error') {
      setOpenSshTerminalError(true);
    } else if (urlTerminal && urlTerminal !== 'error') {
      window.open(urlTerminal, '_blank');
    }
    dispatch(vpsOciActions.openSshTerminal.set(null));
  }, [urlTerminal, dispatch]);

  const handleCloseModal = useCallback(() => {
    setNewInstallation(false);
    setShowChangePasswordModal(false);
  }, []);

  const handleCloseModalSshTerminalError = useCallback(() => {
    setOpenSshTerminalError(false);
  }, []);

  const handleConfirmPassword = useCallback((password, confirmPassword) => {
    if (hostingId) {
      dispatch(vpsOciActions.saveSshPassword.request(hostingId, password, confirmPassword));
    }
  }, [hostingId, dispatch]);

  useEffect(() => {
    if (!vpsOci.password.loading && vpsOci.password.success && showChangePasswordModal) {
      setNewInstallation(false);
      setShowChangePasswordModal(false);
      dispatch(vpsOciActions.saveSshPassword.success(false));
    }
  }, [vpsOci.password.loading, vpsOci.password.success, showChangePasswordModal, dispatch]);

  if (!ssh) return null;

  const fields = [
    {
      name: 'ip',
      label: localeVpsOci.infoSsh.serverIp,
      value: ssh.serverIp,
    },
    {
      name: 'port',
      label: localeVpsOci.infoSsh.serverPort,
      value: ssh.serverPort,
    },
    {
      name: 'user',
      label: localeVpsOci.infoSsh.user,
      value: ssh.user,
    },
  ].filter(item => item.value);

  if (!fields || fields.length === 0) return null;

  return (
    <ContentSection
      ref={ref}
      direction="column"
      align="flex-start"
      justify="flex-start"
      flex={1}
      title={localeVpsOci.infoSsh.title.text}
      tooltip={localeVpsOci.infoSsh.title.tooltip}
      testId="ssh-info-section"
    >
      <Styles.Content>
        <Styles.FieldsList>
          {fields.map(field => (
            <Styles.FieldItem key={field.name} data-testid={`ssh-field-${field.name}`}>
              <Styles.FieldLabel>{field.label}</Styles.FieldLabel>
              <Styles.FieldWrapper>
                <CopySnippet
                  content={field.value}
                  withBorder
                  width="full"
                  testId={`ssh-copy-${field.name}`}
                />
              </Styles.FieldWrapper>
            </Styles.FieldItem>
          ))}
          {isVpsOciOnline && (
            <Styles.FieldItem data-testid="ssh-field-password">
              <Styles.FieldLabel>{localeVpsOci.infoSsh.password}</Styles.FieldLabel>
              <Styles.PasswordWrapper>
                <Button
                  variant="text"
                  label={localeVpsOci.infoSsh.changePassword}
                  onClick={handleChangePassword}
                  testId="ssh-change-password-link"
                  size="medium2"
                />
              </Styles.PasswordWrapper>
            </Styles.FieldItem>
          )}
        </Styles.FieldsList>
        <Styles.TerminalWrapper>
          <Button
            label={localeVpsOci.infoSsh.terminal.buttonLabel}
            variant="primary"
            onClick={isVpsOciOnline ? handleOpenTerminal : undefined}
            testId="ssh-open-terminal-button"
            size="large"
            width="full"
            loading={terminalIsLoading}
            disabled={!isVpsOciOnline}
          />
        </Styles.TerminalWrapper>
      </Styles.Content>
      {(showChangePasswordModal && hostingId) && (
        <ChangePasswordModal
          onClose={handleCloseModal}
          onConfirm={handleConfirmPassword}
          newInstallation={newInstallation}
          isLoading={vpsOci.password.loading}
        />
      )}

      {openSshTerminalError && (
        <Modal
          onClose={handleCloseModalSshTerminalError}
          maxWidth={500}
          title={localeVpsOci.infoSsh.terminal.modal.error.title}
          testId="ssh-terminal-modal-error"
        >
          <Styles.ModalContent>
            <Styles.ModalDescription>
              {localeVpsOci.infoSsh.terminal.modal.error.message}
              <Link
                href={localeVpsOci.infoSsh.terminal.modal.error.link.url}
                target="_blank"
                rel="noopener noreferrer"
                text={localeVpsOci.infoSsh.terminal.modal.error.link.text}
              />
            </Styles.ModalDescription>
            <Styles.ModalButtonWrapper>
              <Button
                variant="primary"
                label={localeVpsOci.infoSsh.terminal.modal.error.confirmButton}
                onClick={handleCloseModalSshTerminalError}
                testId="ssh-terminal-error-confirm-button"
                size="large"
                width="full"
              />
            </Styles.ModalButtonWrapper>
          </Styles.ModalContent>
        </Modal>
      )}
    </ContentSection>
  );
});

SSHAccess.displayName = 'SSHAccess';

export default SSHAccess;
